import {clsx} from 'clsx'
import {
  calculateDiffLineCodeCellGutter,
  cellIdFrom,
  getDiffLineMarkersKey,
  getLineBackgroundColor,
  rowIdFrom,
  trimDiffLineHtml,
} from '../../helpers/line-helpers'
import type {DiffLine, FileDiffReference, LineKey} from '../../types'
import {
  useMarkersAtPosition,
  useSelectedCommentId,
  useStoredCommentAtPosition,
} from '@github-ui/pull-requests/pull-request-store-selectors'
import {noop} from '@github-ui/noop'
import {InlineMarkers} from '../conversations/InlineMarkers'
import React, {memo, useCallback, useId, useMemo, useRef, useState} from 'react'
import {LazyStartConversation, prefetchStartConversation} from '../conversations/LazyStartConversation'
import {SimpleActionBar} from '../SimpleActionBar'
import {
  useDialogModeIsEnabled,
  useDiffLineSelectionInfo,
  useDiffAnchor,
  useDialogModeActions,
  useIsDiffLineActionBarVisible,
  useShowDiffLineActionBar,
  useHideDiffLineActionBar,
  useSetFocusedDiffLineActionBar,
  useClearFocusedDiffLineActionBar,
} from '../../stores/selectors'
import {CommentsPreference} from '@github-ui/diff-view-settings/types'
import type {ViewerData} from '../../conversation-types'
import ExpandableHunkHeaderDiffLine from '../ExpandableHunkHeaderDiffLine'
import {getLineHunkData, type HunkData} from '../../helpers/hunk-data-helpers'
import type {DiffLineType} from '@github-ui/diffs/types'
import {LineTextWrapper} from './LineTextWrapper'
import CommentIndicator from '../grid-cells/CommentIndicator'
import styles from './SimpleDiff.module.css'
import {NoEntryIcon} from '@primer/octicons-react'
import {DiffCodeAlert} from '../DiffCodeAlert'
import {HiddenUnicodeAlert} from '@github-ui/hidden-unicode-banner/HiddenUnicodeAlert'
import {navigateToCommentsUnifiedDiff} from './navigate-to-comments-with-enter-key'
import {lineAnchorFrom, lineOrientationFrom} from '@github-ui/diffs/diff-line-helpers'

const DIMMED_LINE_NUMBER_TYPES: DiffLineType[] = ['CONTEXT', 'INJECTED_CONTEXT']
const EMPTY_CELL_CLASS_NAMES = 'focusable-grid-cell new-diff-line-number empty-diff-line left-side'
const LINE_NUMBER_CELL_CLASS_NAMES = 'focusable-grid-cell new-diff-line-number left-side'

export const SimpleUnifiedDiffRow = memo(function SimpleUnifiedDiffRowComponent({
  diffLine,
  nextDiffLine,
  prevDiffLine,
  filePath,
  avatarURL,
  userLogin,
  viewerCanApplySuggestion,
  handleMouseDownOnNumberCell,
  handleMouseEnterCell,
  handleMouseUpRow,
  handleMouseLeaveAddCommentButton,
  handleMouseDownAddCommentButton,
  copilotChatReference,
  hunksData,
  viewerCanComment,
  shouldDisplayComments,
}: {
  diffLine: DiffLine
  nextDiffLine: DiffLine | undefined
  prevDiffLine: DiffLine | undefined
  filePath: string
  hunksData: HunkData[]
  avatarURL?: string
  userLogin?: string
  viewerCanApplySuggestion?: boolean
  handleMouseDownOnNumberCell: (e: React.MouseEvent) => void
  handleMouseEnterCell: (e: React.MouseEvent) => void
  handleMouseUpRow: () => {shouldStartConversation: boolean}
  handleMouseLeaveAddCommentButton: (e: React.MouseEvent) => void
  handleMouseDownAddCommentButton: (e: React.MouseEvent) => void
  copilotChatReference?: FileDiffReference
  viewerCanComment?: boolean
  shouldDisplayComments?: boolean
}) {
  const isHunkRow = diffLine.type === 'HUNK'
  const showLeftNumber = diffLine.type !== 'ADDITION'
  const showRightNumber = diffLine.type !== 'DELETION'
  const diffLineSelectionInfo = useDiffLineSelectionInfo(diffLine.key)
  const diffAnchor = useDiffAnchor()
  const {enableDialogMode} = useDialogModeActions()
  const rowId = rowIdFrom(diffAnchor, diffLine, diffLine)
  const viewerData = {
    avatarUrl: avatarURL ?? '',
    login: userLogin ?? '',
    viewerCanApplySuggestion: viewerCanApplySuggestion ?? false,
    viewerCanComment: viewerCanComment ?? false,
  }

  // TODO: line selection should be different color in future iteration
  // as part of https://github.com/github/pull-requests/issues/21372
  const lineNumberBackgroundColor = getLineBackgroundColor(diffLine.type, true, diffLineSelectionInfo.isSelected)
  const codeBackgroundColor = getLineBackgroundColor(diffLine.type, false, diffLineSelectionInfo.isSelected)
  const [lineHtml, lineTypeCharacter] = trimDiffLineHtml(diffLine.html, diffLine.type)
  const showLineTypeCharacter = lineTypeCharacter && ['+', '-'].includes(lineTypeCharacter)
  const [isContextMenuOpen, setIsContextMenuOpen] = useState(false)
  const [shouldStartNewConversationWithSuggestedChange, setShouldStartNewConversationWithSuggestedChange] =
    useState(false)
  const {currentHunk, nextHunk, previousHunk} = getLineHunkData(diffLine, hunksData)
  const inlineDialogHeadingId = `inline-dialog-heading-${useId()}`
  const lineSide = diffLine.type !== 'DELETION' ? 'RIGHT' : 'LEFT'
  const lineNumber = lineSide === 'RIGHT' ? diffLine.right : diffLine.left
  const lineMarkersKey = getDiffLineMarkersKey(lineNumber, lineSide)
  const hasStoredComment = useStoredCommentAtPosition(filePath, lineMarkersKey ?? ('' as LineKey))
  const {
    threads: threadsUnfiltered,
    annotations,
    commentsPreference,
  } = useMarkersAtPosition(filePath, lineMarkersKey ?? ('' as LineKey))
  // we do the filtering in the component itself so that the zustand store has a stable reference for the
  // markers at a given position
  const threads = threadsUnfiltered.filter(thread => thread.shouldRenderInDiffLines)
  const hasThreads = threads.length + annotations.length !== 0
  const isSelected = diffLineSelectionInfo.isSelected ?? false
  const commentsAreMinimized = commentsPreference === CommentsPreference.Collapsed
  const [userToggledOverride, setUserToggledOverride] = useState<boolean>(false)
  const commentIndicatorGutterSize = calculateDiffLineCodeCellGutter({
    hasThreads: hasThreads && commentsAreMinimized,
  })

  const selectedCommentId = useSelectedCommentId()

  const hasMatchingComment = useMemo(() => {
    if (selectedCommentId == null || !commentsAreMinimized) return false
    return threads.some(thread =>
      thread.commentsData?.comments.some(comment => comment.databaseId === selectedCommentId),
    )
  }, [selectedCommentId, threads, commentsAreMinimized])

  const commentMinimizeOverride = hasMatchingComment || userToggledOverride

  const cellRef = useRef<HTMLTableCellElement | null>(null)
  // TODO: This needs to be reworked in future iteration
  // as part of https://github.com/github/pull-requests/issues/21372
  const inlineMarkersRef = useRef<HTMLDivElement | null>(null)

  const isLeftSide = diffLine.type === 'DELETION'

  const [showInlineMarkers, setShowInlineMarkers] = useState(
    threads.length > 0 || annotations.length > 0 || hasStoredComment,
  )
  const [isNewConversationDialogOpen, setIsNewConversationDialogOpen] = useState(hasStoredComment ?? false)

  const shouldDisplayInlineComments =
    shouldDisplayComments &&
    (!commentsAreMinimized || commentMinimizeOverride || isNewConversationDialogOpen) &&
    (showInlineMarkers || hasThreads || hasStoredComment || isNewConversationDialogOpen)
  // Grid Navigation cell logic for 3 column grid
  const leftLineNumberCellId = cellIdFrom(rowId, 0)
  const rightLineNumberCellId = cellIdFrom(rowId, 1)
  const codeCellId = cellIdFrom(rowId, 2)
  const isInDialogMode = useDialogModeIsEnabled(codeCellId)
  const lineAnchor = lineAnchorFrom(diffAnchor, lineOrientationFrom(diffLine.type), diffLine.blobLineNumber)

  const handleOnKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      navigateToCommentsUnifiedDiff(e, {
        hasThreads,
        hasStoredComment,
        codeCellId,
        enableDialogMode,
        setIsNewConversationDialogOpen,
      })
    },
    [codeCellId, enableDialogMode, hasThreads, hasStoredComment, setIsNewConversationDialogOpen],
  )
  const showActionBar = useShowDiffLineActionBar()
  const hideActionBar = useHideDiffLineActionBar()
  const setFocusedActionBar = useSetFocusedDiffLineActionBar()
  const clearFocusedActionBar = useClearFocusedDiffLineActionBar()
  const isActionBarVisible = useIsDiffLineActionBarVisible(codeCellId)

  // Handle mouseUp on row - if drag started from add comment button, open conversation dialog
  const handleRowMouseUp = useCallback(() => {
    const {shouldStartConversation} = handleMouseUpRow()

    if (shouldStartConversation) {
      setIsNewConversationDialogOpen(true)
      setShowInlineMarkers(true)
    }
  }, [handleMouseUpRow, setIsNewConversationDialogOpen, setShowInlineMarkers])

  return (
    <tr className="diff-line-row" data-row-selected={isSelected} onMouseUp={handleRowMouseUp}>
      {isHunkRow ? (
        <ExpandableHunkHeaderDiffLine
          currentHunk={currentHunk}
          diffAnchor={diffAnchor}
          diffLine={diffLine}
          isSplit={false}
          nextHunk={nextHunk}
          nextLine={nextDiffLine}
          previousHunk={previousHunk}
          prevLine={prevDiffLine}
        />
      ) : (
        <>
          {showLeftNumber ? (
            <td
              className={clsx(
                LINE_NUMBER_CELL_CLASS_NAMES,
                DIMMED_LINE_NUMBER_TYPES.includes(diffLine.type) && 'diff-line-number-neutral',
              )}
              data-diff-side="left"
              data-diff-line-key={diffLine.key}
              data-first-unified-line-number-cell="true"
              data-grid-cell-id={leftLineNumberCellId}
              data-line-number={diffLine.left}
              data-selected={isSelected}
              role="gridcell"
              style={{backgroundColor: lineNumberBackgroundColor}}
              tabIndex={-1}
              onKeyDown={handleOnKeyDown}
              onMouseDown={handleMouseDownOnNumberCell}
              onMouseEnter={handleMouseEnterCell}
            >
              {isLeftSide && diffLine.hasHiddenUnicode && (
                <DiffCodeAlert>
                  <HiddenUnicodeAlert />
                </DiffCodeAlert>
              )}
              {diffLine.left}
            </td>
          ) : (
            <td
              style={{backgroundColor: lineNumberBackgroundColor}}
              data-grid-cell-id={leftLineNumberCellId}
              data-selected={isSelected}
              className={EMPTY_CELL_CLASS_NAMES}
              role="gridcell"
              onKeyDown={handleOnKeyDown}
              tabIndex={-1}
            />
          )}
          {showRightNumber ? (
            <td
              className={clsx(
                LINE_NUMBER_CELL_CLASS_NAMES,
                DIMMED_LINE_NUMBER_TYPES.includes(diffLine.type) && 'diff-line-number-neutral',
              )}
              data-diff-side="right"
              data-diff-line-key={diffLine.key}
              data-first-unified-line-number-cell="false"
              data-grid-cell-id={rightLineNumberCellId}
              data-line-number={diffLine.right}
              data-selected={isSelected}
              role="gridcell"
              style={{backgroundColor: lineNumberBackgroundColor}}
              tabIndex={-1}
              onKeyDown={handleOnKeyDown}
              onMouseDown={handleMouseDownOnNumberCell}
              onMouseEnter={handleMouseEnterCell}
            >
              {!isLeftSide && diffLine.hasHiddenUnicode && (
                <DiffCodeAlert>
                  <HiddenUnicodeAlert />
                </DiffCodeAlert>
              )}
              {diffLine.right}
            </td>
          ) : (
            <td
              style={{backgroundColor: lineNumberBackgroundColor}}
              data-grid-cell-id={rightLineNumberCellId}
              data-selected={isSelected}
              className={EMPTY_CELL_CLASS_NAMES}
              role="gridcell"
              onKeyDown={handleOnKeyDown}
              tabIndex={-1}
            />
          )}
          <td
            data-grid-cell-id={codeCellId}
            className="diff-text-cell focusable-grid-cell left-side"
            style={{backgroundColor: codeBackgroundColor, paddingRight: commentIndicatorGutterSize}}
            data-diff-line-key={diffLine.key}
            data-line-anchor={lineAnchor}
            data-diff-side={lineSide.toLowerCase()}
            data-line-number={isLeftSide ? diffLine.left : diffLine.right}
            data-selected={isSelected}
            onKeyDown={handleOnKeyDown}
            onMouseEnter={(e: React.MouseEvent) => {
              prefetchStartConversation()
              showActionBar(codeCellId)
              handleMouseEnterCell(e)
            }}
            onMouseLeave={() => hideActionBar(codeCellId)}
            onFocus={() => {
              prefetchStartConversation()
              setFocusedActionBar(codeCellId)
            }}
            onBlur={event => {
              const nextFocus = event.relatedTarget
              if (nextFocus instanceof Node && event.currentTarget.contains(nextFocus)) {
                return
              }
              clearFocusedActionBar()
            }}
            aria-labelledby={isInDialogMode ? inlineDialogHeadingId : undefined}
            role={isInDialogMode ? 'dialog' : 'gridcell'}
            ref={node => {
              if (!isActionBarVisible) {
                cellRef.current = null
              } else {
                cellRef.current = node
              }
            }}
            tabIndex={-1}
          >
            {isInDialogMode && (
              <h1 id={inlineDialogHeadingId} className="sr-only">
                Comment view
              </h1>
            )}
            <LineTextWrapper
              lineHtml={lineHtml}
              lineType={diffLine.type}
              lineTypeCharacter={showLineTypeCharacter ? lineTypeCharacter : undefined}
            />
            {diffLine.displayNoNewLineWarning && <NoEntryIcon size={16} className="fgColor-danger" />}
            {shouldDisplayInlineComments ? (
              <InlineMarkers
                diffLine={diffLine}
                gridCellId={codeCellId}
                gutterSizeOffset={commentIndicatorGutterSize}
                inlineMarkersRef={inlineMarkersRef}
                cellRef={cellRef}
                fileAnchor={diffAnchor}
                // passing a noop because in simplified view we are not dealing with collapsed comments
                dispatchMarkersStatus={noop}
                shouldListenForEscapeKey
                batchingEnabled
                lineMarkersKey={lineMarkersKey}
                annotations={annotations}
                conversationListThreads={threads}
                filePath={filePath}
                onCloseFocusMode={noop}
                isRowSelected={isSelected}
                lineType={diffLine.type}
                shouldStartNewConversationWithSuggestedChange={shouldStartNewConversationWithSuggestedChange}
                viewerData={viewerData as ViewerData}
                onCloseConversationList={() => {
                  setIsNewConversationDialogOpen(false)
                  setShowInlineMarkers(false)
                  setShouldStartNewConversationWithSuggestedChange(false)
                }}
                onDeleteLineComment={() => {
                  setIsNewConversationDialogOpen(false)
                  setShowInlineMarkers(false)
                  setShouldStartNewConversationWithSuggestedChange(false)
                }}
              >
                {(isNewConversationDialogOpen || hasStoredComment) && (
                  <div
                    className={clsx(
                      'border rounded-2 color-border-default',
                      threads.length === 0 ? 'mt-2 mb-1' : 'mt-2 mb-1',
                    )}
                  >
                    <LazyStartConversation
                      diffLine={diffLine}
                      diffAnchor={diffAnchor}
                      batchingEnabled
                      filePath={filePath}
                      focusOnMount={!hasStoredComment}
                      isLeftSide={isLeftSide}
                      isInDialogMode={false}
                      isOpen
                      isRowSelected={isSelected}
                      shouldStartNewConversationWithSuggestedChange={shouldStartNewConversationWithSuggestedChange}
                      viewerData={viewerData}
                      onCloseCommentDialog={() => {
                        cellRef.current?.focus()
                        setIsNewConversationDialogOpen(false)
                        setShowInlineMarkers(false)
                        setShouldStartNewConversationWithSuggestedChange(false)
                      }}
                    />
                  </div>
                )}
              </InlineMarkers>
            ) : null}
            {(isActionBarVisible || isContextMenuOpen) && (
              <SimpleActionBar
                openStartConversationDialog={(value: boolean) => {
                  setShouldStartNewConversationWithSuggestedChange(value)
                  setIsNewConversationDialogOpen(true)
                  setShowInlineMarkers(true)
                }}
                authorAvatarUrl={avatarURL ?? ''}
                authorLogin={userLogin ?? ''}
                ref={cellRef}
                copilotChatReference={copilotChatReference}
                currentHunk={currentHunk}
                diffAnchor={diffAnchor}
                diffLine={diffLine}
                threads={threads}
                annotations={annotations}
                hasDraftComment={hasStoredComment}
                handleMouseLeaveAddCommentButton={handleMouseLeaveAddCommentButton}
                handleMouseDownAddCommentButton={handleMouseDownAddCommentButton}
                isLeftSide={isLeftSide}
                isSplit={false}
                filePath={filePath}
                nextHunk={nextHunk}
                previousHunk={previousHunk}
                shouldDisplayComments={shouldDisplayComments}
                shouldDisplayMinimizedComments={shouldDisplayComments}
                shouldDisplayCollapseComments={commentMinimizeOverride}
                setIsContextMenuOpen={setIsContextMenuOpen}
                cellId={codeCellId}
                contextMenuOpen={isContextMenuOpen}
                isAddCommentBoxOpen={isNewConversationDialogOpen}
                toggleViewingMarkers={() => setUserToggledOverride(prev => !prev)}
                viewerData={{
                  avatarUrl: avatarURL ?? '',
                  diffViewPreference: 'unified',
                  lineSpacingPreference: 'compact',
                  login: userLogin ?? '',
                  commentsPreference,
                  shouldDisplayComments: shouldDisplayComments ?? true,
                  shouldDisplayEditOptions: true,
                  tabSizePreference: 8,
                  viewerCanComment: viewerCanComment ?? false,
                  viewerCanApplySuggestion: viewerCanApplySuggestion ?? false,
                }}
              />
            )}
            {(threads.length > 0 || annotations.length > 0) && commentsAreMinimized && (
              <div
                aria-hidden="true"
                className={clsx('position-absolute top-0 d-flex user-select-none', styles['comment-indicator'])}
              >
                <CommentIndicator threads={threads} annotations={annotations} lineSpacingPreference={'compact'} />
              </div>
            )}
          </td>
        </>
      )}
    </tr>
  )
})
